import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import { useAtomValue, useSetAtom } from 'jotai';
import { RESET } from 'jotai/utils';
import { MouseEvent, useCallback } from 'react';
import { CurrencyCode } from '../../../../../util/currency-util';
import { allCompanyFinancialsState } from '../../../state/CompanyFinancialsState';
import { selectedCompanyIdProfileAtom } from '../../../state/UIStateJ';
import {
  financialsCurrencyOptionsForIdAtom,
  selectedFinancialsCurrencyForIdAtom,
} from '../hooks/FinancialsCurrencyState';
import { ReportingCurrencyChip } from './ReportingCurrencyChip';

/**
 * FD-3875: native <-> USD currency toggle for the financials grid. Changing the
 * selection resets the financials atom, which refetches converted values for
 * the new currency (BE `?currencyCode=`); cells re-render with its symbol.
 */
export function FinancialsCurrencyToggle() {
  const companyId = useAtomValue(selectedCompanyIdProfileAtom);
  const options = useAtomValue(financialsCurrencyOptionsForIdAtom(companyId));
  const selected = useAtomValue(selectedFinancialsCurrencyForIdAtom(companyId));
  const setSelected = useSetAtom(selectedFinancialsCurrencyForIdAtom(companyId));
  const resetFinancials = useSetAtom(allCompanyFinancialsState(companyId));

  const handleChange = useCallback(
    (_event: MouseEvent<HTMLElement>, next: string | null) => {
      if (next && next !== selected) {
        setSelected(next as CurrencyCode);
        resetFinancials(RESET);
      }
    },
    [selected, setSelected, resetFinancials]
  );

  // Native currency is USD (or unknown): nothing to convert, show the read-only chip.
  if (options.length <= 1) {
    return <ReportingCurrencyChip currency={options[0]} />;
  }

  return (
    <ToggleButtonGroup
      aria-label='reporting currency'
      color='secondary'
      exclusive
      size='small'
      value={selected}
      onChange={handleChange}
    >
      {options.map((code) => (
        <ToggleButton key={code} value={code} aria-label={code}>
          {code}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );
}
